import React, { useEffect, useState } from "react";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Text,
	useToast,
} from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { Button } from "newui/components/Button";
import { TextAreaWithLabel } from "newui/components/ui/InputWithLabel";
import { FileUpload } from "newui/components/FileUpload";
import { FILE_UPLOAD_TYPES } from "newui/components/FileUpload/constants/fileUploadTypes";
import type { UploadedFile } from "newui/components/FileUpload/types/UploadedFile.type";
import type { AllowedFileType } from "newui/components/FileUpload/types/AllowedFileType.types";
import EmailPreview from "pages/EmailPreview/EmailPreview";
import { SubmissionUnsuccessfulEmailContent } from "components/email-template/SubmissionUnsuccessfulEmailContent";
import { useRfxMail } from "components/email-template/hooks/useRfxMail";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";
import { useUpsertAwards } from "../../../../hooks/useUpsertAwards";
import type { AwardDecision, AwardRow } from "../../../../types/award.type";
import { ConfirmDecisionDialog } from "./ConfirmDecisionDialog";
import {
	CONFIRM_DECISION_BODY_UNSUCCESSFUL,
	CONFIRM_DECISION_CONFIRM_LABEL,
	CONFIRM_DECISION_TITLE_UNSUCCESSFUL,
	CONTRACT_ACCEPTED_EXTENSIONS,
	CONTRACT_MAX_SIZE_MB,
	isAwardDecisionFinal,
	NOT_AWARDED_LABEL,
	REASON_MAX_LENGTH,
} from "../awardDecisions";

const ACCEPTED_FILE_TYPES: AllowedFileType[] = [...CONTRACT_ACCEPTED_EXTENSIONS];

interface NotAwardedFormValues {
	reason: string;
}

const validationSchema = yup.object().shape({
	reason: yup
		.string()
		.max(REASON_MAX_LENGTH, `Reason must be ${REASON_MAX_LENGTH} characters or fewer`),
});

interface NotAwardedDrawerProps {
	isOpen: boolean;
	suppliers: AwardRow[];
	rfx: Rfx | null;
	rfxId?: number;
	evalId?: number;
	onClose: () => void;
}

/**
 * Marks one or more suppliers as Unsuccessful (PRO-655 supersedes the earlier
 * "Not Awarded" wording). Multiple suppliers are submitted as multiple
 * `unsuccessful` decisions in a single `PUT /awards` request — no separate
 * bulk endpoint. The word "Declined" must never appear here.
 */
export const NotAwardedDrawer: React.FC<NotAwardedDrawerProps> = ({
	isOpen,
	suppliers,
	rfx,
	rfxId,
	evalId,
	onClose,
}) => {
	const toast = useToast();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	// FIX-004: optional attachments for the Not-Awarded email. Uploaded on-the-fly
	// via the shared FileUpload (chunked) flow as orphans scoped to the evaluation
	// (extra_info.rfx_invite_id = null — eval-scoped, shared across the selected
	// suppliers); the same file ids attach to every decision in the batch.
	const [attachmentIds, setAttachmentIds] = useState<number[]>([]);

	const { rfxMail, handleAdditionalMessageChange } = useRfxMail(rfx);
	const upsertAwards = useUpsertAwards(rfxId, evalId);

	const isBulk = suppliers.length > 1;

	useEffect(() => {
		if (isOpen) {
			setIsConfirmOpen(false);
			setAttachmentIds([]);
		}
	}, [isOpen]);

	const handleFilesChange = (files: UploadedFile[]) => {
		setAttachmentIds(
			files.filter((f) => f.id).map((f) => f.id as number)
		);
	};

	const handleCancel = () => {
		setIsConfirmOpen(false);
		setAttachmentIds([]);
		onClose();
	};

	const submitNotAwarded = async (reason: string) => {
		if (suppliers.length === 0) {
			return;
		}
		const comment = reason.trim() ? reason.trim() : null;

		try {
			// Eval-scoped attachments: the same uploaded file ids travel with every
			// selected supplier's decision (no per-invite re-upload).
			const decisions: AwardDecision[] = suppliers.map((supplier) => ({
				rfx_invite_id: supplier.rfx_invite_id,
				award_status: "unsuccessful",
				comment,
				attachment_file_ids: attachmentIds,
			}));

			await upsertAwards.mutateAsync({ decisions });
			setIsConfirmOpen(false);
			toast({
				title: `Marked ${NOT_AWARDED_LABEL}`,
				description: isBulk
					? `${suppliers.length} suppliers were marked ${NOT_AWARDED_LABEL}.`
					: `${suppliers[0].supplier.name} was marked ${NOT_AWARDED_LABEL}.`,
				status: "success",
				duration: 4000,
				isClosable: true,
			});
			onClose();
		} catch (error) {
			setIsConfirmOpen(false);
			if (isAwardDecisionFinal(error)) {
				// useUpsertAwards.onError invalidates the awards + completion caches
				// on this 409, so the table refreshes to the already-final state.
				toast({
					title: "Decision already final",
					description:
						"One or more decisions have already been finalised. The list has been refreshed.",
					status: "warning",
					duration: 5000,
					isClosable: true,
				});
			}
		}
	};

	if (suppliers.length === 0) {
		return null;
	}

	return (
		<Drawer placement="right" isOpen={isOpen} onClose={handleCancel} size="xl">
			<DrawerOverlay />
			<DrawerContent maxWidth="1100px" data-testid="not-awarded-drawer">
				<DrawerHeader className="border-b-2">
					<Flex justifyContent="space-between" align="center">
						<div>
							<p className="text-xl font-semibold text-bg-brand-solid">
								{NOT_AWARDED_LABEL}
							</p>
							<p className="text-sm font-normal text-gray-600">
								{isBulk
									? `${suppliers.length} suppliers ${NOT_AWARDED_LABEL}. A notification email will be sent to each.`
									: `${suppliers[0].supplier.name} ${NOT_AWARDED_LABEL}.`}
							</p>
						</div>
						<Button
							btntype="plain-icon"
							type="button"
							onClick={handleCancel}
							aria-label="Close"
							data-testid="not-awarded-drawer-close"
						>
							<FiX />
						</Button>
					</Flex>
				</DrawerHeader>
				<DrawerBody>
					<Formik<NotAwardedFormValues>
						initialValues={{ reason: "" }}
						validationSchema={validationSchema}
						onSubmit={() => {
							setIsConfirmOpen(true);
						}}
					>
						{({ values, errors, setFieldValue }) => (
							<Form>
								<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
									<div className="lg:col-span-1 space-y-6">
										{isBulk && (
											<div data-testid="not-awarded-supplier-list">
												<p className="text-sm font-medium text-gray-700 mb-1">
													Suppliers ({suppliers.length})
												</p>
												<ul className="text-sm text-gray-600 list-disc pl-5">
													{suppliers.map((supplier) => (
														<li key={supplier.rfx_invite_id}>
															{supplier.supplier.name}
														</li>
													))}
												</ul>
											</div>
										)}

										<TextAreaWithLabel
											label="Reason (optional)"
											name="reason"
											placeholder="Enter an optional reason..."
											maxLength={REASON_MAX_LENGTH}
											showCharacterCount
											value={values.reason}
											errorText={errors.reason}
											hasError={!!errors.reason}
											testId="not-awarded-reason"
											onChange={(e) => {
												setFieldValue("reason", e.target.value);
												handleAdditionalMessageChange(e.target.value);
											}}
										/>

										<div data-testid="not-awarded-attachments">
											<p className="text-sm font-medium text-gray-700 mb-1">
												Attachments{" "}
												<span className="text-gray-500 font-normal">(optional)</span>
											</p>
											<p className="text-xs text-gray-500 mb-2">
												Accepted: {CONTRACT_ACCEPTED_EXTENSIONS.join(", ")} · max{" "}
												{CONTRACT_MAX_SIZE_MB}MB each. Sent with the{" "}
												{NOT_AWARDED_LABEL} email
												{isBulk ? " to every selected supplier." : "."}
											</p>

											<FileUpload
												allowFileTypes={ACCEPTED_FILE_TYPES}
												extraData={{ rfq_id: rfxId, evalId }}
												maxFileSizeMB={CONTRACT_MAX_SIZE_MB}
												multiple
												onChange={handleFilesChange}
												supportsHardDelete={false}
												uploadType={FILE_UPLOAD_TYPES.RFX_AWARD_CONTRACT}
											/>
										</div>

										<Flex gap={3} className="!w-full">
											<Button
												btntype="base"
												type="button"
												width="100%"
												onClick={handleCancel}
												data-testid="not-awarded-cancel"
											>
												Cancel
											</Button>
											<Button
												type="submit"
												width="100%"
												data-testid="not-awarded-confirm"
											>
												Send Email
											</Button>
										</Flex>
									</div>

									<div className="lg:col-span-2">
										<Text className="mb-3 text-lg font-semibold text-primary-900">
											{NOT_AWARDED_LABEL} Email Preview
										</Text>
										{rfxMail && (
											<EmailPreview>
												<SubmissionUnsuccessfulEmailContent rfxMail={rfxMail} />
											</EmailPreview>
										)}
									</div>
								</div>

								<ConfirmDecisionDialog
									isOpen={isConfirmOpen}
									isSubmitting={upsertAwards.isPending}
									title={CONFIRM_DECISION_TITLE_UNSUCCESSFUL}
									body={CONFIRM_DECISION_BODY_UNSUCCESSFUL}
									confirmLabel={CONFIRM_DECISION_CONFIRM_LABEL}
									onCancel={() => setIsConfirmOpen(false)}
									onConfirm={() => submitNotAwarded(values.reason)}
								/>
							</Form>
						)}
					</Formik>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
